import { Product, Refinements } from '@packages/common';
import { GtmInteractor } from '../gtmInteractor';

const FALLBACK_VALUE = 'NV';

export const productClickout = (gtm: GtmInteractor) => (product: Product) => {
  if (product) {
    const { objectID, productId, partner } = product;
    return gtm.push({
      event: 'clickout',
      objectID,
      productID: productId,
      partnerID: partner.id,
    });
  }
};

export interface ProductLoadCompleteEvent {
  quantityOfProducts?: string | number;
  remarketingProductID?: string | string[];
  categoryName?: string;
  categoryID?: string;
  categoryCode?: string;
  categoryPath?: string;
  abTestTitle?: string;
  abTestVersion?: string;
  abTestFactor?: number;
  sortingTest?: string;
}

// TODO: check, this might not be needed because of marketing event.
export const productLoadComplete =
  (gtm: GtmInteractor) =>
  ({
    quantityOfProducts,
    remarketingProductID,
    categoryName,
    categoryID,
    categoryCode,
    categoryPath,
    abTestTitle,
    abTestVersion,
    abTestFactor,
    sortingTest,
  }: ProductLoadCompleteEvent) => {
    return gtm.push({
      event: 'marketing',
      quantityOfProducts: quantityOfProducts || FALLBACK_VALUE,
      remarketingProductID: remarketingProductID || FALLBACK_VALUE,
      categoryName: categoryName || FALLBACK_VALUE,
      categoryID: categoryID || FALLBACK_VALUE,
      categoryCode: categoryCode || FALLBACK_VALUE,
      categoryPath: categoryPath || FALLBACK_VALUE,
      abTestTitle: abTestTitle || FALLBACK_VALUE,
      abTestVersion: abTestVersion || FALLBACK_VALUE,
      abTestFactor: abTestFactor || FALLBACK_VALUE,
      sortingTest: sortingTest || FALLBACK_VALUE,
    });
  };

export const productLoadMore = (gtm: GtmInteractor) => (currentPage: number) => {
  return gtm.push({
    event: 'event',
    eventCategory: 'loadMore',
    eventAction: 'loadMore',
    eventValue: currentPage,
  });
};

export const productSearchStateUpdate = (gtm: GtmInteractor) => (refinements: Refinements) => {
  const { facetFilters, numericFilters } = refinements;
  const facetFiltersData = Object.keys(facetFilters).reduce((acc, next) => {
    if (facetFilters[next].values.length !== 0) {
      const combined = `${facetFilters[next].seoName}:${facetFilters[next].values
        .map((value) => value.seoName)
        .join('.')}`;
      return `${acc}${acc !== '' ? ';' : ''}${combined}`;
    }
    return `${acc}`;
  }, '');
  const numericFiltersData = Object.keys(numericFilters).reduce((acc, next) => {
    const { min, max } = numericFilters[next].value;
    if (min && max) {
      const combined = `${numericFilters[next].seoName}:${min}-${max}`;
      return `${acc}${acc !== '' ? ';' : ''}${combined}`;
    }
    return `${acc}`;
  }, '');
  return gtm.push({
    event: 'event',
    eventLabel: 'filter',
    eventAction: 'filter',
    eventCategory: 'filter',
    filterFacette:
      facetFiltersData === '' && numericFiltersData === ''
        ? FALLBACK_VALUE
        : `${facetFiltersData !== '' ? `${facetFiltersData};` : ''}${
            numericFiltersData !== '' ? `${numericFiltersData};` : ''
          }`.replace(/;$/, ''),
  });
};

export interface ProductUpdateEvent {
  localFilter?: string;
  quantityOfProducts?: string | number;
  remarketingProductID?: string | string[];
}

// local product filter
// TODO: check if its needed after marketing event
export const productUpdate = (gtm: GtmInteractor) => {
  return ({ localFilter, quantityOfProducts, remarketingProductID }: ProductUpdateEvent) => {
    return gtm.push({
      event: 'event',
      eventCategory: 'filter',
      eventAction: 'localFilter',
      eventLabel: 'localFilter',
      localFilter: localFilter || FALLBACK_VALUE,
      quantityOfProducts: quantityOfProducts || FALLBACK_VALUE,
      remarketingProductID: remarketingProductID || FALLBACK_VALUE,
    });
  };
};

export interface AddToWishListEventPayload {
  productId: string;
  title: string;
}

export interface GTMAddToWishListPayload extends AddToWishListEventPayload {
  isLocallyFiltered: boolean;
}

export const addToWishlist = (gtm: GtmInteractor) => (payload: GTMAddToWishListPayload) =>
  gtm.push({
    event: 'event',
    eventCategory: 'wishlist',
    eventLabel: 'addToWishlist',
    eventAction: payload.productId,
    name: payload.title,
    localProduct: payload.isLocallyFiltered,
  });

export interface GTMRemoveFromWishListPayload extends AddToWishListEventPayload {
  isLocallyFiltered: boolean;
}

export const removeFromWishlist = (gtm: GtmInteractor) => (payload: GTMRemoveFromWishListPayload) =>
  gtm.push({
    event: 'event',
    eventCategory: 'wishlist',
    eventLabel: 'removeFromWishlist',
    eventAction: payload.productId,
    name: payload.title,
    localProduct: payload.isLocallyFiltered,
  });
